// src/pages/MeetingReport.tsx
//
// Native replacement for the "Meeting Report Writer" Google Form + Zapier flow (the
// GForm titled "CBAM FUND Event - 3 - Report"). Investment-team gated (route + server
// RLS via has_research_capability('author')). Lookup-driven (fund search pre-fill) and
// wired to the meeting-report-submit edge fn through the same offline-safe outbox as
// MeetingRequest / MeetingBooked.
//
// A report is written against a booked meeting: the picker (get_meeting_reportable_bookings)
// links booking_id + asana_task_gid and pre-fills identity + meeting_date. A standalone
// option lets a report be filed with no linked meeting (the submit fn then creates a bare
// meeting_bookings row of form_kind='report'). On submit the ~20 manual performance cells
// are folded into the strict perf_cumulative / perf_calendar JSONB shape; server-side
// calcs (alpha, subjective sum, year labels) are derived from these. See the build
// contract §1/§3/§9 for the authoritative shapes.

import { useEffect, useMemo, useState } from "react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useMeetingLookups, type MeetingFundOption } from "@/hooks/useMeetingLookups";
import { enqueueMeeting, postMeeting, removeMeeting, drainMeetings, MeetingPostError } from "@/lib/meetings/meetingOutbox";
import { useIsOnline } from "@/hooks/useIsOnline";
import { Loader2 } from "lucide-react";

// The submit fn targets a different edge function than Request/Booked.
const SUBMIT_SLUG = "meeting-report-submit";
const STANDALONE = "__standalone__";

// Performance periods — MUST match the build contract §1 JSONB keys exactly.
const CUM_PERIODS = ["3m", "6m", "1Yr", "3Yr", "5Yr"] as const;
const CAL_PERIODS = ["YTD", "Yr1", "Yr2", "Yr3", "Yr4"] as const;
type CumPeriod = (typeof CUM_PERIODS)[number];
type CalPeriod = (typeof CAL_PERIODS)[number];

// Investment-process options. The free-text field beside it (inv_process_other) is ALWAYS
// available: it is the doc's {{Process_Notes}} source for every option (see
// 20260724130000_meeting_reports.sql — "BJ (free text; also renders as Process_Notes)").
// Picking "Other" additionally promotes that same text to {{Inv_Philosophy}} via
// invPhilosophy(process, processOther).
const PROCESS_OPTIONS = [
  "Bottom-up",
  "Top-down",
  "Blend / Hybrid",
  "Quantitative / Systematic",
  "Thematic",
  "Other",
];

const SUBJECTIVE_FIELDS: Array<{ key: SubjKey; label: string; hint: string }> = [
  { key: "fm", label: "Fund Manager", hint: "Conviction in the manager themselves." },
  { key: "process", label: "Process", hint: "Repeatability / robustness of the investment process." },
  { key: "confidence", label: "Confidence", hint: "Our overall confidence in the strategy." },
  { key: "team", label: "Team", hint: "Depth and stability of the wider team." },
  { key: "relationship", label: "Relationship", hint: "Quality of the CBAM ↔ house relationship." },
  { key: "presentation", label: "Presentation", hint: "Clarity / quality of the meeting itself." },
];
type SubjKey = "fm" | "process" | "confidence" | "team" | "relationship" | "presentation";

// A row returned by get_meeting_reportable_bookings (author's recent booked/requested
// meetings). Cast via (supabase.rpc as any) — this RPC is newer than committed types.ts.
interface ReportableBooking {
  id: string;
  meeting_date: string | null;
  fund_name: string | null;
  fund_house_name: string | null;
  fund_manager: string | null;
  fund_class: string | null;
  isin: string | null;
  ocf_pct: number | null;
  amc: number | null;
  bdm_name: string | null;
  asana_task_gid: string | null;
  asana_task_url: string | null;
  has_report: boolean;
}

// Normalise a fund identifier for comparison (ISIN/SEDOL are case-insensitive, no spaces/dashes).
const normId = (v: string | null | undefined): string => (v ?? "").toUpperCase().replace(/[\s-]/g, "");

// Empty / non-numeric string → null; otherwise the parsed JS number (contract §1: cells are
// numbers or null). Used for every numeric input on submit.
const numOrNull = (v: string | null | undefined): number | null => {
  if (v == null) return null;
  const t = String(v).trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const emptyPeriodMap = <K extends string>(keys: readonly K[]): Record<K, string> =>
  Object.fromEntries(keys.map((k) => [k, ""])) as Record<K, string>;

const MeetingReport = () => {
  // SUBSCRIBE, do not read the module cache. useIsOnline's cache is only updated while a
  // component calling THIS HOOK is mounted (its setOnline call sites all live in the hook's
  // effect), and nothing else on this route mounts it — so getIsOnline() here would be frozen
  // at module-evaluation time. See the note in the submit catch below.
  const online = useIsOnline();
  const { toast } = useToast();
  const { user, staffMember } = useAuth();
  const { meetingFunds, isLoading } = useMeetingLookups();

  // Section 1 — linked meeting + creator
  const [bookings, setBookings] = useState<ReportableBooking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [selectedBookingId, setSelectedBookingId] = useState<string>(STANDALONE);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [asanaTaskGid, setAsanaTaskGid] = useState<string | null>(null);
  const [reportCreatorName, setReportCreatorName] = useState("");

  // Section 2 — fund identity (pre-filled, editable)
  const [fundManager, setFundManager] = useState(""); // the house / manager
  const [fundName, setFundName] = useState("");
  const [showFundList, setShowFundList] = useState(false);
  const [fundClass, setFundClass] = useState("");
  // Persisted to meeting_reports.isin (migration 20260811120000). It used to be captured and then
  // thrown away ("context only") — the strongest fund identity key in the flow, discarded: the picker
  // SEARCHES on it and meeting_fund_lookup is keyed on it, so without it a filed report can only be
  // joined back to the fund universe by free-text fund name.
  const [fundIsin, setFundIsin] = useState("");
  const [fundBdm, setFundBdm] = useState("");
  const [fundAmc, setFundAmc] = useState("");
  const [fundOcf, setFundOcf] = useState("");

  // Section 3 — meeting
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingAttendees, setMeetingAttendees] = useState(""); // CBAM attendees (free text)
  const [fundAttendees, setFundAttendees] = useState("");        // fund-side attendees (free text)
  const [attendeeNote, setAttendeeNote] = useState("");
  const [zoomLink, setZoomLink] = useState("");                  // Zoom recording link → report.zoom_link
  const [readaiLink, setReadaiLink] = useState("");              // Read.ai transcription link → report.readai_link
  const [documents, setDocuments] = useState<File[]>([]);        // supporting docs → attached to the Asana task

  // Section 4 — investment profile
  const [invProcess, setInvProcess] = useState("");
  const [invProcessOther, setInvProcessOther] = useState("");
  const [tiltMicro, setTiltMicro] = useState("");
  const [tiltSmall, setTiltSmall] = useState("");
  const [tiltMid, setTiltMid] = useState("");
  const [tiltLarge, setTiltLarge] = useState("");
  const [tiltMega, setTiltMega] = useState("");
  const [invGeoFocus, setInvGeoFocus] = useState("");
  const [invGeoAlloc, setInvGeoAlloc] = useState("");
  const [invIndustryFocus, setInvIndustryFocus] = useState("");
  const [invIndustryAlloc, setInvIndustryAlloc] = useState("");
  const [fundAssetType, setFundAssetType] = useState("");
  const [hedgeAvailability, setHedgeAvailability] = useState("");
  const [fundType, setFundType] = useState("");

  // Section 5 — subjective (0–5 each)
  const [subj, setSubj] = useState<Record<SubjKey, string>>({
    fm: "", process: "", confidence: "", team: "", relationship: "", presentation: "",
  });

  // Section 6 — performance
  const [benchmarkName, setBenchmarkName] = useState("");
  const [cumFund, setCumFund] = useState<Record<CumPeriod, string>>(emptyPeriodMap(CUM_PERIODS));
  const [cumBm, setCumBm] = useState<Record<CumPeriod, string>>(emptyPeriodMap(CUM_PERIODS));
  const [calFund, setCalFund] = useState<Record<CalPeriod, string>>(emptyPeriodMap(CAL_PERIODS));
  const [calBm, setCalBm] = useState<Record<CalPeriod, string>>(emptyPeriodMap(CAL_PERIODS));

  const [submitting, setSubmitting] = useState(false);

  const isOther = invProcess === "Other";

  // Default the report creator name from the logged-in staff member, falling back to email.
  useEffect(() => {
    if (reportCreatorName) return;
    const name = staffMember
      ? `${staffMember.first_name ?? ""} ${staffMember.last_name ?? ""}`.trim()
      : "";
    setReportCreatorName(name || user?.email || "");
  }, [staffMember, user, reportCreatorName]);

  // Load the author's reportable booked meetings (author-gated RPC; empty for non-authors).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBookingsLoading(true);
      try {
        const { data, error } = await (supabase.rpc as any)("get_meeting_reportable_bookings");
        if (error) throw error;
        if (!cancelled) setBookings(((data ?? []) as ReportableBooking[]));
      } catch {
        if (!cancelled) setBookings([]);
      } finally {
        if (!cancelled) setBookingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Drain any queued (offline) submissions on mount + whenever the tab comes online.
  useEffect(() => {
    void drainMeetings();
    const onOnline = () => void drainMeetings();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  // Filtered fund matches: substring on name OR ISIN, case-insensitive, capped at 50.
  const fundMatches = useMemo(() => {
    const q = fundName.trim().toLowerCase();
    if (!q) return [];
    const qn = normId(q);
    return meetingFunds
      .filter(
        (f) =>
          f.fund_name.toLowerCase().includes(q) ||
          (!!f.isin && normId(f.isin).includes(qn)),
      )
      .slice(0, 50);
  }, [meetingFunds, fundName]);

  // Calendar year labels shown as grid headers — mirrors calendarYearLabels() in report-calc.ts
  // (Yr1 = meeting year − 1 … Yr4 = meeting year − 4). Year parsed from the first 4 chars of the
  // 'YYYY-MM-DD' date to avoid a TZ off-by-one; falls back to the current year when unset.
  const yearBase = useMemo(() => {
    if (meetingDate && /^\d{4}/.test(meetingDate)) return Number(meetingDate.slice(0, 4));
    return new Date().getFullYear();
  }, [meetingDate]);

  const calLabel = (p: CalPeriod): string =>
    p === "YTD" ? `Year to Date (${yearBase})` : String(yearBase - Number(p.slice(2)));

  // Live subjective total (null → 0), constant denominator of 30 (mirrors subjectiveSum()).
  const subjectiveSum = useMemo(
    () => SUBJECTIVE_FIELDS.reduce((acc, f) => acc + (numOrNull(subj[f.key]) ?? 0), 0),
    [subj],
  );

  const pickFund = (f: MeetingFundOption) => {
    setFundName(f.fund_name);
    setFundClass(f.fund_class ?? "");
    setFundIsin(f.isin ?? "");
    if (f.ter_pct != null) setFundOcf(String(f.ter_pct));
    if (f.amc != null) setFundAmc(String(f.amc));
    if (f.fund_house) setFundManager(f.fund_house);
    setShowFundList(false);
  };

  const onSelectBooking = (value: string) => {
    setSelectedBookingId(value);
    if (value === STANDALONE) {
      setBookingId(null);
      setAsanaTaskGid(null);
      return;
    }
    const b = bookings.find((x) => x.id === value);
    if (!b) return;
    setBookingId(b.id);
    setAsanaTaskGid(b.asana_task_gid ?? null);
    // Pre-fill identity + meeting date (all remain editable).
    setFundManager(b.fund_manager ?? b.fund_house_name ?? "");
    setFundName(b.fund_name ?? "");
    setFundClass(b.fund_class ?? "");
    setFundIsin(b.isin ?? "");
    setFundBdm(b.bdm_name ?? "");
    setFundOcf(b.ocf_pct != null ? String(b.ocf_pct) : "");
    setFundAmc(b.amc != null ? String(b.amc) : "");
    setMeetingDate(b.meeting_date ?? "");
  };

  const setSubjField = (key: SubjKey, value: string) =>
    setSubj((prev) => ({ ...prev, [key]: value }));

  const bookingLabel = (b: ReportableBooking): string => {
    const date = b.meeting_date ?? "no date";
    const house = b.fund_house_name ?? b.fund_manager ?? "";
    const parts = [date, house, b.fund_name].filter(Boolean).join(" · ");
    return `${parts}${b.has_report ? "  (report already filed)" : ""}`;
  };

  const resetForm = () => {
    setSelectedBookingId(STANDALONE);
    setBookingId(null);
    setAsanaTaskGid(null);
    // reportCreatorName is intentionally retained (same author files multiple reports).
    setFundManager(""); setFundName(""); setShowFundList(false); setFundClass("");
    setFundIsin(""); setFundBdm(""); setFundAmc(""); setFundOcf("");
    setMeetingDate(""); setMeetingAttendees(""); setFundAttendees(""); setAttendeeNote("");
    setZoomLink(""); setReadaiLink(""); setDocuments([]);
    setInvProcess(""); setInvProcessOther("");
    setTiltMicro(""); setTiltSmall(""); setTiltMid(""); setTiltLarge(""); setTiltMega("");
    setInvGeoFocus(""); setInvGeoAlloc(""); setInvIndustryFocus(""); setInvIndustryAlloc("");
    setFundAssetType(""); setHedgeAvailability(""); setFundType("");
    setSubj({ fm: "", process: "", confidence: "", team: "", relationship: "", presentation: "" });
    setBenchmarkName("");
    setCumFund(emptyPeriodMap(CUM_PERIODS)); setCumBm(emptyPeriodMap(CUM_PERIODS));
    setCalFund(emptyPeriodMap(CAL_PERIODS)); setCalBm(emptyPeriodMap(CAL_PERIODS));
  };

  const buildReport = () => {
    // Strict §1 JSONB shape: { period: { fund, bm } } with numbers or null.
    const perf_cumulative = Object.fromEntries(
      CUM_PERIODS.map((p) => [p, { fund: numOrNull(cumFund[p]), bm: numOrNull(cumBm[p]) }]),
    );
    const perf_calendar = Object.fromEntries(
      CAL_PERIODS.map((p) => [p, { fund: numOrNull(calFund[p]), bm: numOrNull(calBm[p]) }]),
    );

    return {
      fund_manager: fundManager || null,
      fund_name: fundName || null,
      fund_type: fundType || null,
      hedge_availability: hedgeAvailability || null,
      fund_class: fundClass || null,
      isin: fundIsin || null,
      fund_bdm: fundBdm || null,
      fund_amc: numOrNull(fundAmc),
      fund_ocf: numOrNull(fundOcf),
      fund_asset_type: fundAssetType || null,
      meeting_date: meetingDate || null,
      meeting_attendees: meetingAttendees || null,
      fund_attendees: fundAttendees || null,
      attendee_note: attendeeNote || null,
      zoom_link: zoomLink || null,
      readai_link: readaiLink || null,
      inv_process: invProcess || null,
      // WAS: `isOther ? (invProcessOther || null) : null`. inv_process_other is the doc's
      // {{Process_Notes}} source for EVERY process option, not an 'Other'-only field, so gating the
      // send on the dropdown silently discarded the analyst's process notes for the other five
      // options and {{Process_Notes}} rendered blank. Always send it when non-empty.
      // {{Inv_Philosophy}} is unchanged: invPhilosophy() still only reads this text when the
      // process is 'Other', and otherwise renders the dropdown value.
      inv_process_other: invProcessOther || null,
      inv_geo_focus: invGeoFocus || null,
      inv_geo_alloc: invGeoAlloc || null,
      inv_industry_focus: invIndustryFocus || null,
      inv_industry_alloc: invIndustryAlloc || null,
      tilt_micro_cap: tiltMicro || null,
      tilt_small_cap: tiltSmall || null,
      tilt_mid_cap: tiltMid || null,
      tilt_large_cap: tiltLarge || null,
      tilt_mega_cap: tiltMega || null,
      subj_fm: numOrNull(subj.fm),
      subj_process: numOrNull(subj.process),
      subj_confidence: numOrNull(subj.confidence),
      subj_team: numOrNull(subj.team),
      subj_relationship: numOrNull(subj.relationship),
      subj_presentation: numOrNull(subj.presentation),
      benchmark_name: benchmarkName || null,
      perf_cumulative,
      perf_calendar,
    };
  };

  const validSubjective = (): boolean =>
    SUBJECTIVE_FIELDS.every((f) => {
      const n = numOrNull(subj[f.key]);
      return n == null || (n >= 0 && n <= 5);
    });

  const handleSubmit = async () => {
    // Minimal required-field validation.
    if (!fundManager) {
      toast({ title: "Investment manager / house is required", variant: "destructive" }); return;
    }
    if (!fundName) {
      toast({ title: "Fund name is required", variant: "destructive" }); return;
    }
    if (!validSubjective()) {
      toast({ title: "Subjective scores must be between 0 and 5", variant: "destructive" }); return;
    }

    const key = crypto.randomUUID();
    setSubmitting(true);
    try {
      // Upload supporting documents to the private research-attachments bucket (best-effort,
      // requires online). Path is uid-prefixed to satisfy the bucket's write RLS. Mirrors
      // MeetingBooked exactly; the worker attaches these to the Asana task.
      const attachment_keys: string[] = [];
      if (documents.length > 0 && user) {
        for (const file of documents) {
          const path = `${user.id}/meetings/${key}/${file.name}`;
          const { error } = await supabase.storage
            .from("research-attachments")
            .upload(path, file, { upsert: false });
          if (!error) attachment_keys.push(path);
        }
      }

      const payload: Record<string, unknown> = {
        booking_id: bookingId ?? undefined,
        asana_task_gid: asanaTaskGid ?? undefined,
        report_creator_name: reportCreatorName || undefined,
        report: buildReport(),
        attachment_keys,
      };

      // Stage first (survives a mid-request failure / offline), then attempt the post.
      enqueueMeeting(key, payload, SUBMIT_SLUG);
      const data = await postMeeting(key, payload, SUBMIT_SLUG);
      removeMeeting(key);

      toast({
        title: "Report queued",
        description:
          data?.status === "queued" || data?.report_id
            ? "The appraisal document is being generated and filed to Drive + Asana."
            : "Submitted; the report will be generated shortly.",
      });
      resetForm();
    } catch (e) {
      // WHAT THIS CATCH MAY AND MAY NOT CLAIM.
      //
      // It used to render "Saved offline" for EVERY failure without ever reading `e`, so a
      // server refusal told the analyst their regulated report was safely queued when it had
      // just been rejected. A first fix then guarded on getIsOnline() — copied from
      // src/components/capture/CaptureSheet.tsx:330 — but copied the GUARD WITHOUT THE
      // SUBSCRIPTION, and that is the trap worth recording:
      //
      //   useIsOnline's `onlineCache` (src/hooks/useIsOnline.ts:16) is a module-level value whose
      //   ONLY writer is setOnline(), and every call site of setOnline (:74,:76,:88,:90,:91) sits
      //   inside useIsOnline()'s useEffect. The cache therefore only tracks connectivity while a
      //   component calling the HOOK is mounted. CaptureSheet does exactly that at :130. These
      //   three pages did not, and nothing in their route (partnerOpsRoutes -> ProtectedRoute ->
      //   Layout -> PartnerNavbar) mounts it either — the only global mount is OfflineBanner in
      //   src/components/mobile/MobileShell.tsx:237. So getIsOnline() here was frozen at whatever
      //   navigator.onLine said at module-evaluation time, i.e. effectively always true, and the
      //   offline branch was dead code.
      //
      // So this page now SUBSCRIBES (`const online = useIsOnline()` in the component) and
      // classifies on the typed error the outbox throws.
      const status = e instanceof MeetingPostError ? e.status : null;
      const message = e instanceof Error ? e.message : "Submission failed.";

      // No HTTP status reached us at all (transport died), or we know we are offline: this is the
      // one case that has genuinely been queued for later.
      if (status === null || !online) {
        toast({
          title: "Saved offline",
          description:
            "Your report is queued and will submit automatically when you're back online.",
        });
      } else {
        // The server answered and refused. Say so, and name the status — the page CAN now, because
        // meetingOutbox throws MeetingPostError carrying `.status` (meetingOutbox.ts:152-163,
        // thrown at :198). Earlier comments here claimed the status was unavailable; that was
        // true of an older postMeeting and is no longer.
        //
        // ⚠️ THE ITEM IS DELIBERATELY LEFT IN THE OUTBOX. An earlier fix deleted it, which
        // contradicted the outbox's own stated policy (meetingOutbox.ts:213-214: an item "KEEPS
        // its payload and its place in the outbox, never deleted, because the payload is work
        // somebody typed and this is the only copy of it") and threw away RETRYABLE failures
        // (5xx/408/429) before drainMeetings could apply the bounded retry it now implements.
        // A permanent 4xx is parked by the drain on its first pass (:268,:286) and never
        // re-posted, so leaving it queued cannot produce a duplicate booking.
        toast({
          variant: "destructive",
          title: "Report not filed",
          description:
            `${message} (HTTP ${status}). Your answers are still on this form; nothing has been ` +
            `filed. It stays in the outbox and will not be re-sent automatically if the server ` +
            `refused it permanently.`,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 py-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title="File a Fund Manager Meeting Report — CBAM"
        description="Write up a fund-manager meeting: generates the Fund Appraisal document, files it to Drive and Asana for the Cape Berkshire Asset Management investment team."
        path="/meeting-report"
        noindex
      />
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Meeting Report Writer</CardTitle>
            <CardDescription>
              Write up a completed fund-manager meeting. Generates the Fund Appraisal document, exports a
              PDF to Drive, attaches it to the Asana task, and notifies the investment team.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-10">
              {/* 1 — Linked meeting + creator */}
              <section className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-1">Linked meeting</h3>
                <div>
                  <Label htmlFor="booking" className="text-sm font-medium">Booked meeting</Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    Pick the meeting this report is written against to link the Asana task and pre-fill the
                    fund details, or file a standalone report.
                  </p>
                  <Select value={selectedBookingId} onValueChange={onSelectBooking}>
                    <SelectTrigger id="booking">
                      <SelectValue placeholder={bookingsLoading ? "Loading meetings…" : "Select a booked meeting"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value={STANDALONE}>No linked meeting (standalone)</SelectItem>
                      {bookings.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{bookingLabel(b)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {bookingId && asanaTaskGid && (
                    <p className="text-xs text-muted-foreground mt-1">Linked Asana task {asanaTaskGid}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="creator" className="text-sm font-medium">Report created by</Label>
                  <Input
                    id="creator"
                    value={reportCreatorName}
                    onChange={(e) => setReportCreatorName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
              </section>

              {/* 2 — Fund identity */}
              <section className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-1">Fund identity</h3>
                <div>
                  <Label htmlFor="fundManager" className="text-sm font-medium">Investment manager / house *</Label>
                  <Input
                    id="fundManager"
                    value={fundManager}
                    onChange={(e) => setFundManager(e.target.value)}
                    placeholder="e.g. Baillie Gifford"
                  />
                </div>
                <div>
                  <Label htmlFor="fundName" className="text-sm font-medium">Fund name *</Label>
                  <div className="relative">
                    <Input
                      id="fundName"
                      value={fundName}
                      onChange={(e) => { setFundName(e.target.value); setShowFundList(true); }}
                      onFocus={() => setShowFundList(true)}
                      placeholder="Search funds by name or ISIN, or type to add manually"
                      autoComplete="off"
                    />
                    {showFundList && fundName.trim() && fundMatches.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border bg-popover shadow-md">
                        {fundMatches.map((f) => (
                          <button
                            type="button"
                            key={f.id}
                            onClick={() => pickFund(f)}
                            className="block w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                          >
                            <span className="font-medium">{f.fund_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {f.fund_house ? ` · ${f.fund_house}` : ""}
                              {f.fund_class ? ` · ${f.fund_class}` : ""}
                              {f.isin ? ` · ${f.isin}` : ""}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pick from the list to autofill class / OCF / AMC / ISIN, or just type the fund name if it
                    isn't listed.
                  </p>
                  {fundIsin && <p className="text-xs text-muted-foreground mt-1">ISIN {fundIsin}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fundClass" className="text-sm font-medium">Fund class</Label>
                    <Input id="fundClass" value={fundClass} onChange={(e) => setFundClass(e.target.value)} placeholder="e.g. B Acc" />
                  </div>
                  <div>
                    <Label htmlFor="fundBdm" className="text-sm font-medium">BDM</Label>
                    <Input id="fundBdm" value={fundBdm} onChange={(e) => setFundBdm(e.target.value)} placeholder="Business development manager" />
                  </div>
                  <div>
                    <Label htmlFor="fundAmc" className="text-sm font-medium">AMC (%)</Label>
                    <Input id="fundAmc" type="number" step="any" value={fundAmc} onChange={(e) => setFundAmc(e.target.value)} placeholder="e.g. 0.75" />
                  </div>
                  <div>
                    <Label htmlFor="fundOcf" className="text-sm font-medium">OCF (%)</Label>
                    <Input id="fundOcf" type="number" step="any" value={fundOcf} onChange={(e) => setFundOcf(e.target.value)} placeholder="e.g. 0.82" />
                  </div>
                </div>
              </section>

              {/* 3 — Meeting */}
              <section className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-1">Meeting</h3>
                <div>
                  <Label htmlFor="meetingDate" className="text-sm font-medium">Meeting date</Label>
                  <Input id="meetingDate" type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="meetingAttendees" className="text-sm font-medium">CBAM attendees</Label>
                  <Input
                    id="meetingAttendees"
                    value={meetingAttendees}
                    onChange={(e) => setMeetingAttendees(e.target.value)}
                    placeholder="e.g. Mark Insley, Sam Hallett"
                  />
                </div>
                <div>
                  <Label htmlFor="fundAttendees" className="text-sm font-medium">Fund-side attendees</Label>
                  <Input
                    id="fundAttendees"
                    value={fundAttendees}
                    onChange={(e) => setFundAttendees(e.target.value)}
                    placeholder="Who attended from the fund house"
                  />
                </div>
                <div>
                  <Label htmlFor="attendeeNote" className="text-sm font-medium">Attendee note / meeting summary</Label>
                  <Textarea
                    id="attendeeNote"
                    value={attendeeNote}
                    onChange={(e) => setAttendeeNote(e.target.value)}
                    rows={5}
                    placeholder="Free-text write-up of the meeting. This is posted as the Asana story."
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="zoomLink" className="text-sm font-medium">Zoom recording link</Label>
                    <Input
                      id="zoomLink"
                      type="url"
                      value={zoomLink}
                      onChange={(e) => setZoomLink(e.target.value)}
                      placeholder="https://…zoom.us/rec/…"
                    />
                  </div>
                  <div>
                    <Label htmlFor="readaiLink" className="text-sm font-medium">Read.ai transcription link</Label>
                    <Input
                      id="readaiLink"
                      type="url"
                      value={readaiLink}
                      onChange={(e) => setReadaiLink(e.target.value)}
                      placeholder="https://app.read.ai/…"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="documents" className="text-sm font-medium">Supporting documents</Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    Attached to the Asana task (e.g. fund factsheets, meeting slides).
                  </p>
                  <Input
                    id="documents"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                    onChange={(e) => setDocuments(Array.from(e.target.files ?? []))}
                    className="mt-1"
                  />
                  {documents.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{documents.length} file(s) selected</p>
                  )}
                </div>
              </section>

              {/* 4 — Investment profile */}
              <section className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-1">Investment profile</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="invProcess" className="text-sm font-medium">Investment process</Label>
                    <Select value={invProcess} onValueChange={setInvProcess}>
                      <SelectTrigger id="invProcess"><SelectValue placeholder="Select process" /></SelectTrigger>
                      <SelectContent>
                        {PROCESS_OPTIONS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Shown for EVERY process option. It was previously gated behind `isOther`, which
                      made the doc's {{Process_Notes}} unreachable for the other five options — there
                      was no field to type them into, and anything already typed was then dropped at
                      submit. Only the wording changes on 'Other', because that is the one case where
                      this text also becomes {{Inv_Philosophy}}. */}
                  <div>
                    <Label htmlFor="invProcessOther" className="text-sm font-medium">
                      {isOther ? "Process (other) — please describe" : "Process notes"}
                    </Label>
                    <Input
                      id="invProcessOther"
                      value={invProcessOther}
                      onChange={(e) => setInvProcessOther(e.target.value)}
                      placeholder={isOther ? "Describe the process" : "Free-text notes on the investment process"}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">Market-cap tilt</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Describe the tilt at each band (e.g. a weight, "Overweight", or leave blank).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    <div>
                      <Label htmlFor="tiltMicro" className="text-xs">Micro Cap</Label>
                      <Input id="tiltMicro" value={tiltMicro} onChange={(e) => setTiltMicro(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="tiltSmall" className="text-xs">Small Cap</Label>
                      <Input id="tiltSmall" value={tiltSmall} onChange={(e) => setTiltSmall(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="tiltMid" className="text-xs">Mid Cap</Label>
                      <Input id="tiltMid" value={tiltMid} onChange={(e) => setTiltMid(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="tiltLarge" className="text-xs">Large Cap</Label>
                      <Input id="tiltLarge" value={tiltLarge} onChange={(e) => setTiltLarge(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="tiltMega" className="text-xs">Mega Cap</Label>
                      <Input id="tiltMega" value={tiltMega} onChange={(e) => setTiltMega(e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="invGeoFocus" className="text-sm font-medium">Geographic focus</Label>
                    <Input id="invGeoFocus" value={invGeoFocus} onChange={(e) => setInvGeoFocus(e.target.value)} placeholder="e.g. Global, US, Emerging Markets" />
                  </div>
                  <div>
                    <Label htmlFor="invGeoAlloc" className="text-sm font-medium">Geographic allocation</Label>
                    <Input id="invGeoAlloc" value={invGeoAlloc} onChange={(e) => setInvGeoAlloc(e.target.value)} placeholder="e.g. 60% US / 25% EU / 15% EM" />
                  </div>
                  <div>
                    <Label htmlFor="invIndustryFocus" className="text-sm font-medium">Industry focus</Label>
                    <Input id="invIndustryFocus" value={invIndustryFocus} onChange={(e) => setInvIndustryFocus(e.target.value)} placeholder="e.g. Technology, Healthcare" />
                  </div>
                  <div>
                    <Label htmlFor="invIndustryAlloc" className="text-sm font-medium">Industry allocation</Label>
                    <Input id="invIndustryAlloc" value={invIndustryAlloc} onChange={(e) => setInvIndustryAlloc(e.target.value)} placeholder="e.g. 30% Tech / 20% Financials" />
                  </div>
                  <div>
                    <Label htmlFor="fundAssetType" className="text-sm font-medium">Asset type</Label>
                    <Input id="fundAssetType" value={fundAssetType} onChange={(e) => setFundAssetType(e.target.value)} placeholder="e.g. Equity, Fixed Income, Multi-asset" />
                  </div>
                  <div>
                    <Label htmlFor="hedgeAvailability" className="text-sm font-medium">Hedge availability</Label>
                    <Input id="hedgeAvailability" value={hedgeAvailability} onChange={(e) => setHedgeAvailability(e.target.value)} placeholder="e.g. Hedged / Unhedged share classes" />
                  </div>
                  <div>
                    <Label htmlFor="fundType" className="text-sm font-medium">Fund type</Label>
                    <Input id="fundType" value={fundType} onChange={(e) => setFundType(e.target.value)} placeholder="e.g. OEIC, Unit Trust, Investment Trust" />
                  </div>
                </div>
              </section>

              {/* 5 — Subjective */}
              <section className="space-y-4">
                <div className="flex items-center justify-between border-b pb-1">
                  <h3 className="text-lg font-semibold">Subjective scores</h3>
                  <span className="text-sm text-muted-foreground">Total {subjectiveSum} / 30</span>
                </div>
                <p className="text-xs text-muted-foreground">Rate each dimension from 0 (poor) to 5 (excellent).</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {SUBJECTIVE_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label htmlFor={`subj-${f.key}`} className="text-sm font-medium">{f.label}</Label>
                      <Select value={subj[f.key]} onValueChange={(v) => setSubjField(f.key, v)}>
                        <SelectTrigger id={`subj-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {[0, 1, 2, 3, 4, 5].map((n) => (
                            <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">{f.hint}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* 6 — Performance */}
              <section className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-1">Performance</h3>
                <div>
                  <Label htmlFor="benchmark" className="text-sm font-medium">Benchmark name</Label>
                  <Input id="benchmark" value={benchmarkName} onChange={(e) => setBenchmarkName(e.target.value)} placeholder="e.g. IA Global sector / MSCI ACWI" />
                </div>

                {/* Cumulative grid — Fund / Benchmark × 3m/6m/1Yr/3Yr/5Yr */}
                <div>
                  <Label className="text-sm font-medium">Cumulative performance (%)</Label>
                  <p className="text-xs text-muted-foreground mb-2">Enter percentage returns; alpha is computed server-side. Leave blank where unknown.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[520px] text-sm">
                      <thead>
                        <tr>
                          <th className="text-left font-medium p-2"></th>
                          {CUM_PERIODS.map((p) => (
                            <th key={p} className="font-medium p-2 text-center">{p}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th className="text-left font-medium p-2 whitespace-nowrap">Fund</th>
                          {CUM_PERIODS.map((p) => (
                            <td key={p} className="p-1">
                              <Input
                                type="number"
                                step="any"
                                aria-label={`Fund cumulative ${p}`}
                                value={cumFund[p]}
                                onChange={(e) => setCumFund((prev) => ({ ...prev, [p]: e.target.value }))}
                                className="text-right"
                              />
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <th className="text-left font-medium p-2 whitespace-nowrap">Benchmark</th>
                          {CUM_PERIODS.map((p) => (
                            <td key={p} className="p-1">
                              <Input
                                type="number"
                                step="any"
                                aria-label={`Benchmark cumulative ${p}`}
                                value={cumBm[p]}
                                onChange={(e) => setCumBm((prev) => ({ ...prev, [p]: e.target.value }))}
                                className="text-right"
                              />
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Calendar grid — Fund / Benchmark × YTD/Yr1..Yr4 (computed year labels) */}
                <div>
                  <Label className="text-sm font-medium">Calendar-year performance (%)</Label>
                  <p className="text-xs text-muted-foreground mb-2">Year labels are derived from the meeting date.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] text-sm">
                      <thead>
                        <tr>
                          <th className="text-left font-medium p-2"></th>
                          {CAL_PERIODS.map((p) => (
                            <th key={p} className="font-medium p-2 text-center whitespace-nowrap">{calLabel(p)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th className="text-left font-medium p-2 whitespace-nowrap">Fund</th>
                          {CAL_PERIODS.map((p) => (
                            <td key={p} className="p-1">
                              <Input
                                type="number"
                                step="any"
                                aria-label={`Fund calendar ${calLabel(p)}`}
                                value={calFund[p]}
                                onChange={(e) => setCalFund((prev) => ({ ...prev, [p]: e.target.value }))}
                                className="text-right"
                              />
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <th className="text-left font-medium p-2 whitespace-nowrap">Benchmark</th>
                          {CAL_PERIODS.map((p) => (
                            <td key={p} className="p-1">
                              <Input
                                type="number"
                                step="any"
                                aria-label={`Benchmark calendar ${calLabel(p)}`}
                                value={calBm[p]}
                                onChange={(e) => setCalBm((prev) => ({ ...prev, [p]: e.target.value }))}
                                className="text-right"
                              />
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleSubmit} disabled={submitting || isLoading} className="bg-green-600 hover:bg-green-700">
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {submitting ? "Filing…" : "File report"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MeetingReport;
